import { msg } from "@lingui/core/macro";
import { createSignal, Show } from "solid-js";
import { Eye, Loader2, Save, Sparkles, X } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api, createApiMutation } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useCanWriteContent } from "../use-admin-capabilities";

type EmailTemplate = components["schemas"]["EmailTemplate"];

interface Props {
  /** null => modo creación. */
  template: EmailTemplate | null;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Editor de plantilla de email con preview en vivo y asistencia IA (campaña #11).
 * La IA es real (Claude vía backend); si el admin no configuró ANTHROPIC_API_KEY
 * el backend responde 503 y se muestra un aviso claro.
 */
export function EmailTemplateEditor(props: Props) {
  const { t } = useI18n();
  const isEdit = () => props.template != null;
  /**
   * Pantalla ENTERA de escritura: deshabilitar doce controles dejaría un editor
   * muerto. Sin `content.write` se abre en MODO LECTURA (campos inertes, sin
   * IA/Preview/Guardar) porque el cuerpo de la plantilla ya viaja en el GET de
   * la lista y `read_only` puede verlo. Lo que sí se corta de raíz es la
   * CREACIÓN (`EmailTemplateManager` oculta la entrada): ahí no hay nada que leer.
   */
  const canWriteContent = useCanWriteContent();
  const readOnly = () => !canWriteContent();

  // El editor se monta de nuevo por cada plantilla (Show + key distinta), así que
  // inicializar las señales desde props en el constructor es intencionado.
  /* eslint-disable solid/reactivity */
  const [key, setKey] = createSignal(props.template?.key ?? "");
  const [name, setName] = createSignal(props.template?.name ?? "");
  const [category, setCategory] = createSignal(props.template?.category ?? "marketing");
  const [subject, setSubject] = createSignal(props.template?.subject ?? "");
  const [body, setBody] = createSignal(props.template?.bodyHtml ?? "");
  const [isActive, setIsActive] = createSignal(props.template?.isActive ?? true);
  /* eslint-enable solid/reactivity */
  const [aiPrompt, setAiPrompt] = createSignal("");
  const [aiError, setAiError] = createSignal<string | null>(null);
  const [preview, setPreview] = createSignal<{ subject: string; bodyHtml: string } | null>(null);

  const createMutation = createApiMutation(
    async (vars: components["schemas"]["CreateEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates", { body: vars }),
  );
  const updateMutation = createApiMutation(
    async (vars: components["schemas"]["UpdateEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates/update", { body: vars }),
  );
  const previewMutation = createApiMutation(
    async (vars: components["schemas"]["PreviewEmailTemplateDto"]) =>
      api.POST("/api/admin/email/templates/preview", { body: vars }),
  );
  const aiMutation = createApiMutation(async (vars: components["schemas"]["AiDraftRequestDto"]) =>
    api.POST("/api/admin/email/templates/ai-draft", { body: vars }),
  );

  const handlePreview = async () => {
    try {
      const r = await previewMutation.mutateAsync({
        subject: subject(),
        bodyHtml: body(),
        context: {
          Name: "Ana",
          Link: "https://famidex.app/x",
          UnsubscribeLink: "https://famidex.app/u",
        },
      });
      setPreview({ subject: r.subject, bodyHtml: r.bodyHtml });
    } catch (err) {
      console.error("preview error", err);
    }
  };

  const handleAi = async () => {
    setAiError(null);
    try {
      const draft = await aiMutation.mutateAsync({
        prompt: aiPrompt(),
        category: category(),
        currentSubject: subject() || undefined,
        currentBodyHtml: body() || undefined,
        variables: ["Name", "Link", "UnsubscribeLink"],
      });
      setSubject(draft.subject);
      setBody(draft.bodyHtml);
    } catch (err) {
      // 503 cuando falta ANTHROPIC_API_KEY (límite externo IA).
      setAiError(
        t(msg`AI assistance is unavailable. Ask the admin to configure ANTHROPIC_API_KEY.`),
      );
      console.error("ai-draft error", err);
    }
  };

  const handleSave = async () => {
    try {
      if (isEdit() && props.template) {
        await updateMutation.mutateAsync({
          id: props.template.id,
          name: name(),
          subject: subject(),
          bodyHtml: body(),
          variables: props.template.variables ?? [],
          isActive: isActive(),
        });
      } else {
        await createMutation.mutateAsync({
          key: key(),
          category: category(),
          name: name(),
          subject: subject(),
          bodyHtml: body(),
          variables: [],
        });
      }
      await props.onSaved();
    } catch (err) {
      console.error("save error", err);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-border bg-field px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {readOnly()
            ? t(msg`View Template`)
            : isEdit()
              ? t(msg`Edit Template`)
              : t(msg`New Template`)}
        </CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <Show when={!isEdit()}>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Key`)}</span>
              <input
                class={inputClass}
                value={key()}
                readOnly={readOnly()}
                onInput={(e) => setKey(e.currentTarget.value)}
              />
            </label>
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Category`)}</span>
              <select
                class={inputClass}
                value={category()}
                disabled={readOnly()}
                onChange={(e) => setCategory(e.currentTarget.value)}
              >
                <option value="transactional">{t(msg`Transactional`)}</option>
                <option value="marketing">{t(msg`Marketing`)}</option>
                <option value="product">{t(msg`Product`)}</option>
              </select>
            </label>
          </div>
        </Show>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">{t(msg`Name`)}</span>
          <input
            class={inputClass}
            value={name()}
            readOnly={readOnly()}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </label>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">{t(msg`Subject`)}</span>
          <input
            class={inputClass}
            value={subject()}
            readOnly={readOnly()}
            onInput={(e) => setSubject(e.currentTarget.value)}
          />
        </label>

        <Show when={isEdit()}>
          <div class="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <span class="text-sm text-muted-foreground">{t(msg`Active`)}</span>
            <Switch
              checked={isActive()}
              disabled={readOnly()}
              onCheckedChange={(v) => setIsActive(v)}
            />
          </div>
        </Show>

        <label class="block text-sm">
          <span class="mb-1 block text-muted-foreground">
            {t(msg`Body (HTML, Handlebars variables like {{Name}})`)}
          </span>
          <textarea
            class={`${inputClass} min-h-40 font-mono`}
            value={body()}
            readOnly={readOnly()}
            onInput={(e) => setBody(e.currentTarget.value)}
          />
        </label>

        {/* Asistencia IA: genera contenido → sólo con content.write. */}
        <Show when={canWriteContent()}>
          <div class="rounded-lg border border-dashed border-border p-3">
            <div class="mb-2 flex items-center gap-2 text-sm font-medium">
              <Sparkles class="h-4 w-4 text-primary" />
              {t(msg`AI assist`)}
            </div>
            <textarea
              class={`${inputClass} min-h-16`}
              placeholder={t(
                msg`Describe the email you want, e.g. "a friendly welcome email for new users"`,
              )}
              value={aiPrompt()}
              onInput={(e) => setAiPrompt(e.currentTarget.value)}
            />
            <div class="mt-2 flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                class="gap-1"
                disabled={aiMutation.isPending || !aiPrompt()}
                onClick={handleAi}
              >
                <Show when={aiMutation.isPending} fallback={<Sparkles class="h-4 w-4" />}>
                  <Loader2 class="h-4 w-4 animate-spin" />
                </Show>
                {t(msg`Generate with AI`)}
              </Button>
              <Show when={aiError()}>
                <span class="text-xs text-destructive">{aiError()}</span>
              </Show>
            </div>
          </div>
        </Show>

        <Show when={preview()}>
          {(p) => (
            <div class="rounded-lg border border-border p-3">
              <p class="mb-2 text-xs font-medium text-muted-foreground">
                {t(msg`Preview`)}: {p().subject}
              </p>
              {/* eslint-disable-next-line solid/no-innerhtml */}
              <div class="prose prose-sm max-w-none" innerHTML={p().bodyHtml} />
            </div>
          )}
        </Show>

        <div class="flex items-center gap-2">
          {/* Preview y Save son POST: sin la capacidad, 403. Se ocultan. */}
          <Show when={canWriteContent()}>
            <Button
              variant="outline"
              size="sm"
              class="gap-1"
              onClick={handlePreview}
              disabled={previewMutation.isPending}
            >
              <Eye class="h-4 w-4" />
              {t(msg`Preview`)}
            </Button>
            <Button
              size="sm"
              class="gap-1"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              <Save class="h-4 w-4" />
              {t(msg`Save`)}
            </Button>
          </Show>
          <Button variant="ghost" size="sm" class="gap-1" onClick={() => props.onCancel()}>
            <X class="h-4 w-4" />
            {readOnly() ? t(msg`Close`) : t(msg`Cancel`)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
